import { Accordion, AccordionDetails, AccordionSummary, Box, Typography, createStyles, makeStyles } from '@material-ui/core';
import cn from 'classnames';
import * as React from 'react';
import Modal from '../../../components/Modal/Modal';
import RenderText from '../../../dictionaries/RenderText';
import { BankType, NegotiationOption, NegotiationType, OutDebt } from '../../../generated/graphql';
import { BogotaPaymentGuides } from '../../../utils/BogotaPaymentGuides';
import { getEntityLabel } from '../../../utils/financialEntitiesList';
import { getAgreementFromFunder } from '../../../utils/getAgreementFromFunder';
import { getMainOutDebtFromParent } from '../../../utils/getMainOutDebtFromParent';
import { getNegBooleans } from '../../../utils/getNegBooleans';
import { JcapPaymentGuides } from '../../../utils/JcapPaymentGuides';
import { TranquiTheme } from '../../App/theme';

interface AnotherPaymentChannelsModalProps {
  openModal: boolean;
  onClose: () => void;
  isDav?: boolean;
  isDaviplata?: boolean;
  isBeta?: boolean;
  isJcap?: boolean;
  isTranqui?: boolean;
  isBogota?: boolean;
  outDebt: OutDebt;
  isApex?: boolean;
  isPra?: boolean;
  isClaro?: boolean;
  negOption: NegotiationOption;
}

const AnotherPaymentChannelsModal: React.FC<AnotherPaymentChannelsModalProps> = (props) => {
  const {
    openModal,
    onClose,
    isDav,
    isDaviplata,
    isBeta,
    isJcap,
    outDebt,
    negOption,
    isTranqui,
    isBogota,
    isApex,
    isPra,
    isClaro,
  } = props;
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const classes = useStyles();
  const [ expanded, setExpanded ] = React.useState<string | false>('pse');
  const handleChangeAccordion = (panel: string) => (event: any, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  const { isCatchUp, isMovistarMoneyInvoice } = getNegBooleans(outDebt, negOption);

  const handleClose = () => {
    setExpanded('pse');
    onClose();
  };

  const movistarChannels = (
    <>
      <Typography>
        <span>Si realizas el pago en un punto físico,
          debes agregar a la referencia de pago un 5 al final,
          para que tu pago sea dirigido al crédito con Movistar Money
          y se haga efectivo el acuerdo de pago.
        </span>
      </Typography>
      <Accordion
        className={cn(classes.accordion, expanded === 'pagaTodo' && classes.accordionSelected)}
        onChange={handleChangeAccordion('pagaTodo')}
        expanded={expanded === 'pagaTodo'}
      >
        <AccordionSummary className={classes.accordionTitleCard}>
          <Typography className={classes.accordionTitle}>Paga todo</Typography>
        </AccordionSummary>
      </Accordion>
      {outDebt.extra?.movistarInvoice !== 'Si' && (
        <Accordion
          className={cn(classes.accordion, expanded === 'baloto' && classes.accordionSelected)}
          onChange={handleChangeAccordion('baloto')}
          expanded={expanded === 'baloto'}
        >
          <AccordionSummary className={classes.accordionTitleCard}>
            <Typography className={classes.accordionTitle}>Vía Baloto</Typography>
          </AccordionSummary>
        </Accordion>
      )}
      <Accordion
        className={cn(classes.accordion, expanded === 'efecty' && classes.accordionSelected)}
        onChange={handleChangeAccordion('efecty')}
        expanded={expanded === 'efecty'}
      >
        <AccordionSummary className={classes.accordionTitleCard}>
          <Typography className={classes.accordionTitle}>Efecty</Typography>
        </AccordionSummary>
      </Accordion>
      <Accordion
        className={cn(classes.accordion, expanded === 'suRed' && classes.accordionSelected)}
        onChange={handleChangeAccordion('suRed')}
        expanded={expanded === 'suRed'}
      >
        <AccordionSummary className={classes.accordionTitleCard}>
          <Typography className={classes.accordionTitle}>Su Red</Typography>
        </AccordionSummary>
      </Accordion>
      <Accordion
        className={cn(classes.accordion, expanded === 'redServi' && classes.accordionSelected)}
        onChange={handleChangeAccordion('redServi')}
        expanded={expanded === 'redServi'}
      >
        <AccordionSummary className={classes.accordionTitleCard}>
          <Typography className={classes.accordionTitle}>Red Servi</Typography>
        </AccordionSummary>
      </Accordion>
      <Accordion
        className={cn(classes.accordion, expanded === 'gana' && classes.accordionSelected)}
        onChange={handleChangeAccordion('gana')}
        expanded={expanded === 'gana'}
      >
        <AccordionSummary className={classes.accordionTitleCard}>
          <Typography className={classes.accordionTitle}>Gana</Typography>
        </AccordionSummary>
      </Accordion>
      <Accordion
        className={cn(classes.accordion, expanded === 'ganaGana' && classes.accordionSelected)}
        onChange={handleChangeAccordion('ganaGana')}
        expanded={expanded === 'ganaGana'}
      >
        <AccordionSummary className={classes.accordionTitleCard}>
          <Typography className={classes.accordionTitle}>Gana Gana</Typography>
        </AccordionSummary>
      </Accordion>
      <Accordion
        className={cn(classes.accordion, expanded === 'puntored' && classes.accordionSelected)}
        onChange={handleChangeAccordion('puntored')}
        expanded={expanded === 'puntored'}
      >
        <AccordionSummary className={classes.accordionTitleCard}>
          <Typography className={classes.accordionTitle}>Puntored</Typography>
        </AccordionSummary>
      </Accordion>
    </>
  );

  const movistarCatchUpChannels = (
    <>
      <Typography>Paso a paso para realizar el pago:
      </Typography>
      <RenderText textKey={isMovistarMoneyInvoice ? 'negotiation_CatchUp_invoice_payment_steps' : 'negotiation_CatchUp_payment_steps'} isMarkdown />
    </>
  );

  const daviviendaChannels = (
    <>
      <Accordion
        className={cn(classes.accordion, expanded === 'channels' && classes.accordionSelected)}
        onChange={handleChangeAccordion('channels')}
        expanded={expanded === 'channels'}
      >
        <AccordionSummary className={classes.accordionTitleCard}>
          <Typography className={classes.accordionTitle}>
            Canales de pago autorizados por Davivienda
          </Typography>
        </AccordionSummary>
        <AccordionDetails className={classes.details}>
          <li>Oficinas Davivienda</li>
          <li><a href="www.davivienda.com">www.davivienda.com</a></li>
          <li>App Davivienda móvil</li>
          <li>Pago PSE</li>
          <li>Cajeros Multifuncionales</li>
        </AccordionDetails>
      </Accordion>
      <Accordion
        className={cn(classes.accordion, expanded === 'online' && classes.accordionSelected)}
        onChange={handleChangeAccordion('online')}
        expanded={expanded === 'online'}
      >
        <AccordionSummary className={classes.accordionTitleCard}>
          <Typography className={classes.accordionTitle}>
            Para realizar pago en línea
          </Typography>
        </AccordionSummary>
        <AccordionDetails className={classes.details}>
          <ol>
            <li>Ingresa al portal de pagos en línea</li>
            <li>Da clic a <strong>"Productos Davivienda"</strong>,
              después a <strong>"Cartera Tarjetas de Crédito" o "Cartera Créditos"</strong>
            </li>
            <li>Ingresas el número de tu producto, ahí verás un resumen del estado
              actual de tu deuda y una casilla en la que debes modificar el valor a cancelar;
              ingresas el valor del abono y sigues las instrucciones.
              <ul>
                <li>
                  Mientras el Banco aplica la negociación, es posible que se generen débitos
                  automáticos si cuentas con dinero en tu cuenta,
                  por lo cual te aconsejamos retirar tu dinero o pasarlo a una cuenta bolsillo.
                </li>
              </ul>
            </li>
          </ol>
        </AccordionDetails>
      </Accordion>
    </>
  );

  const isTotalPayment = negOption.type === NegotiationType.TotalPayment;
  const daviplataChannels = (
    <>
      <ul>
        {!isTotalPayment && <li>Recargando Daviplata</li>}
        <li>Corresponsal bancario PuntoRed</li>
        <li>Corresponsal bancario Reval</li>
        <li>Corresponsal bancario Conred</li>
      </ul>
    </>
  );

  const betaChannels = (
    <>
      <Typography className={classes.accordionTitle}>
        Pago en oficina Davivienda
      </Typography>
      <Typography className="my-1">
        Dirígete a cualquier oficina del Banco Davivienda,{' '}
        solicita el formato de convenio empresarial y diligencia la siguiente información:
      </Typography>
      <Typography>
        <strong>Nombre del convenio:</strong> Cobranzas Beta
      </Typography>
      <Typography>
        <strong>Código de convenio: </strong>
        CONVENIO {isDav ? 'DAVIVIENDA' : 'BBVA'} <span className="t-mono"> {isDav ? '1111921' : '1111931'}</span>
      </Typography>
      <Typography>
        <strong>Referencia 1:</strong> Número de Cédula
      </Typography>
      <Typography>
        <strong>Referencia 2:</strong> Número de producto
      </Typography>
    </>
  );

  const od = getMainOutDebtFromParent(outDebt);
  const jcapAgreements = getAgreementFromFunder(od);
  const isDetailedAgreementType = (type: typeof jcapAgreements[number]['type']) => [ 'Daviplata', 'AvalPayCenter' ].includes(type);
  const jcapChannels = !!jcapAgreements.length ? (
    <>
      {jcapAgreements.map(({ type, value, bank, link }) => (
        <>
          {!isDetailedAgreementType(type) && (
            <Accordion
              className={cn(classes.accordion, expanded === `${type}-${value}-${bank || ''}` && classes.accordionSelected)}
              onChange={handleChangeAccordion(`${type}-${value}-${bank || ''}`)}
              expanded={expanded === `${type}-${value}-${bank || ''}`}
            >
              <AccordionSummary className={classes.accordionTitleCard}>
                <Typography className={classes.accordionTitle}>
                  {type} {!!bank ? getEntityLabel(bank as BankType, (outDebt.brand as string) || undefined) : ''}
                </Typography>
              </AccordionSummary>
              <AccordionDetails className={classes.details}>
                <li><strong>Número de convenio:</strong> {value}</li>
                <li><strong>Número de referencia:</strong> Número de cédula </li>
              </AccordionDetails>
            </Accordion>
          )}
          {isDetailedAgreementType(type) && (
            <JcapPaymentGuides agreement={{ type, value, bank, link }} />
          )}
        </>
      ))}
    </>
  ) : undefined;

  const bogotaChannels = (
    <BogotaPaymentGuides negOption={negOption} />
  );

  const noChannels = (
    <>
      <Typography className="my-1">
        No hay canales de pago disponibles para esta entidad.
      </Typography>
    </>
  );

  const praChannels = (
    <>
      <Typography className="my-1">
        Puedes realizar el pago de tu cuota mensual a través de los siguientes medios:
      </Typography>
      <Accordion
        className={cn(classes.accordion, expanded === 'praBancarias' && classes.accordionSelected)}
        onChange={handleChangeAccordion('praBancarias')}
        expanded={expanded === 'praBancarias'}
      >
        <AccordionSummary className={classes.accordionTitleCard}>
          <Typography className={classes.accordionTitle}>
            Pago en sucursales bancarias
          </Typography>
        </AccordionSummary>
        <AccordionDetails className={classes.details}>
          <Typography className="my-1">
            Presentando el recibo de pago en cualquiera de las siguientes entidades autorizadas:
          </Typography>
          <ul>
            <li>
              El recibo lo puedes descargar ingresando a{' '}
              <a href="http://www.micovinoc.com" target="_blank" rel="noopener noreferrer">
                <strong>www.micovinoc.com</strong>
              </a>
              , seleccionando la opción <strong>"Gestiona tu pago"</strong> y luego{' '}
              <strong>"Paga en línea"</strong>, donde deberás ingresar tu documento de identidad
              o lo recibirás en tu correo electrónico registrado.
            </li>
            <li>Bancolombia</li>
            <li>Banco de Bogotá</li>
            <li>Banco AV Villas</li>
            <li>Banco de Occidente</li>
            <li>Davivienda</li>
          </ul>
        </AccordionDetails>
      </Accordion>
      <Accordion
        className={cn(classes.accordion, expanded === 'praCorresponsales' && classes.accordionSelected)}
        onChange={handleChangeAccordion('praCorresponsales')}
        expanded={expanded === 'praCorresponsales'}
      >
        <AccordionSummary className={classes.accordionTitleCard}>
          <Typography className={classes.accordionTitle}>
            Pago en corresponsales no bancarios
          </Typography>
        </AccordionSummary>
        <AccordionDetails className={classes.details}>
          <Typography className="my-1">Puedes realizar el pago en los siguientes puntos:</Typography>
          <ul>
            <li>Puntored</li>
            <li>4-72</li>
            <li>Punto de Pago</li>
          </ul>
          <Typography className="my-1"><strong>Para este medio debes utilizar:</strong></Typography>
          <ul>
            <li><strong>Convenio:</strong> PRA GROUP #951697</li>
            <li><strong>Número de referencia:</strong> 6002896350</li>
          </ul>
        </AccordionDetails>
      </Accordion>
      <Accordion
        className={cn(classes.accordion, expanded === 'praPSE' && classes.accordionSelected)}
        onChange={handleChangeAccordion('praPSE')}
        expanded={expanded === 'praPSE'}
      >
        <AccordionSummary className={classes.accordionTitleCard}>
          <Typography className={classes.accordionTitle}>
            Pago en línea (PSE)
          </Typography>
        </AccordionSummary>
        <AccordionDetails className={classes.details}>
          <Typography>
            Puedes realizar la transferencia desde el botón de pagos PSE ingresando a:{' '}
            <a href="http://www.micovinoc.com" target="_blank" rel="noopener noreferrer">
              <strong>www.micovinoc.com</strong>
            </a>
          </Typography>
        </AccordionDetails>
      </Accordion>
      <Accordion
        className={cn(classes.accordion, expanded === 'praCheque' && classes.accordionSelected)}
        onChange={handleChangeAccordion('praCheque')}
        expanded={expanded === 'praCheque'}
      >
        <AccordionSummary className={classes.accordionTitleCard}>
          <Typography className={classes.accordionTitle}>
            Pago mediante cheque
          </Typography>
        </AccordionSummary>
        <AccordionDetails className={classes.details}>
          <Typography>
            Los pagos en cheque deben girarse a nombre de:{' '}
            <strong>PRA Group Holding Colombia S.A.S.</strong>
          </Typography>
        </AccordionDetails>
      </Accordion>
    </>
  );

  const claroChannels = (
    <>
      <Typography className="my-1">
        Opciones de pago rápidas y seguras:
      </Typography>
      <ul>
        <li>App Mi Claro</li>
        <li>Claro Pay</li>
        <li>Portal Pagos Claro</li>
        <li>Botón PSE</li>
        <li>Canales digitales de tu banco</li>
      </ul>
    </>
  );

  let content: JSX.Element | undefined = daviviendaChannels;
  if (isDav) content = daviviendaChannels;
  if (isDaviplata) content = daviplataChannels;
  if (isBeta) content = betaChannels;
  if (isJcap) content = jcapChannels;
  if (isApex) content = isCatchUp ? movistarCatchUpChannels : movistarChannels;
  if (isPra) content = praChannels;
  if (isClaro) content = claroChannels;
  if (isTranqui && isBogota) content = bogotaChannels;

  if (!content) content = noChannels;

  const modalTitle = (isTranqui && isBogota) || (isApex && isCatchUp)
    ? 'Guías de pago'
    : 'Otros canales de pago';

  return (
    <Modal
      onClose={handleClose}
      open={openModal}
      maxWidth="sm"
      scroll="paper"
      data-test="cy-anotherPaymentChannels-modal"
      className={classes.root}
      showCloseBtn
      fullWidth
      title={modalTitle}
    >
      <Box className={classes.root}>
        {content}
      </Box>
    </Modal>
  );
};

const useStyles = makeStyles((theme: TranquiTheme) => createStyles({
  root: {
    padding: theme.spacing(3, 0),
  },
  accordion: {
    boxShadow: theme.shadows[5],
    borderBottom: `solid 1px ${theme.palette.indigo.main}40`,
    borderRadius: theme.spacing(0.5),
    overflow: 'hidden',
  },
  accordionSelected: {
    border: `solid 1px ${theme.palette.indigo.main}40`,
    '& $accordionTitleCard': {
      background: theme.palette.indigo.main,
      '& $accordionTitle': {
        color: theme.palette.pearl.main,
      },
    },
  },
  accordionTitleCard: {
    transition: 'all .4s',
  },
  accordionTitle: {
    ...theme.typography.body1,
    fontWeight: 600,
    color: theme.palette.indigo.main,
    transition: 'all .4s',
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    backgroundColor: theme.palette.marble.main,
    padding: theme.spacing(2),
    '& typography': {
      margin: 0,
    },
  },
  generalTextTitle: {
    ...theme.typography.body1,
    lineHeight: 'inherit',
  },
  dividerTitle: {
    marginTop: theme.spacing(2),
  },
  generalText: {
    ...theme.typography.subtitle1,
    lineHeight: 'inherit',
  },
  littleText: {
    ...theme.typography.subtitle2,
    lineHeight: 'inherit',
  },
  tinyText: {
    ...theme.typography.subtitle2,
    color: theme.palette.grey[500],
    '& a': {
      color: theme.palette.lavender.main,
      fontWeight: 600,
    },
  },
}));

export default AnotherPaymentChannelsModal;
